import { collection, doc, getDoc, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'

export type ScopedChatChannelDoc = {
  id: string
  data: Record<string, unknown>
}

export type SubscribeChatChannelsScope = {
  restricted: boolean
  uid: string
}

export interface SubscribeChatChannelsOptions {
  permissionDeniedKeys?: Set<string>
  onUpdate: (publicChannels: ScopedChatChannelDoc[], privateChannels: ScopedChatChannelDoc[], ready: boolean) => void
  onPermissionDenied?: () => void
}

export const chatChannelPermissionKey = (workspaceId: string, uid: string, restricted: boolean): string =>
  `${workspaceId}:${uid}:${restricted ? 'restricted' : 'unrestricted'}`

export const subscribeChatChannels = (
  workspaceId: string,
  scope: SubscribeChatChannelsScope,
  options: SubscribeChatChannelsOptions,
): Unsubscribe => {
  const { restricted, uid } = scope
  const { permissionDeniedKeys = new Set<string>(), onUpdate, onPermissionDenied } = options

  const permissionKey = chatChannelPermissionKey(workspaceId, uid, restricted)
  if (permissionDeniedKeys.has(permissionKey)) {
    onPermissionDenied?.()
    return () => {}
  }

  let publicChannels: ScopedChatChannelDoc[] = []
  let privateChannels: ScopedChatChannelDoc[] = []
  let publicChannelsReady = false
  let privateChannelsReady = false

  const channelCollection = collection(firestoreDb, 'workspaces', workspaceId, 'channels')

  const unsubscribePublic = onSnapshot(
    query(
      channelCollection,
      restricted
        ? where('scopeRead', 'array-contains', uid)
        : where('type', '==', 'public'),
    ),
    (snapshot) => {
      publicChannelsReady = true
      publicChannels = snapshot.docs.map((channelDoc) => ({
        id: channelDoc.id,
        data: channelDoc.data() as Record<string, unknown>,
      }))
      onUpdate(publicChannels, privateChannels, publicChannelsReady && privateChannelsReady)
    },
    (error: unknown) => {
      const err = error as { code?: string }
      if (err?.code === 'permission-denied') {
        permissionDeniedKeys.add(permissionKey)
        onPermissionDenied?.()
        console.info('[ChatContext] chat channels unavailable for this session.')
        return
      }
      console.warn('[Firestore listener failed] public chat channels', workspaceId, error)
    },
  )

  const unsubscribePrivate = onSnapshot(
    query(channelCollection, where(`roles.${uid}`, 'in', ['admin', 'member'])),
    (snapshot) => {
      privateChannelsReady = true
      privateChannels = snapshot.docs.map((channelDoc) => ({
        id: channelDoc.id,
        data: channelDoc.data() as Record<string, unknown>,
      }))
      onUpdate(publicChannels, privateChannels, publicChannelsReady && privateChannelsReady)
    },
    (error: unknown) => {
      const err = error as { code?: string }
      if (err?.code === 'permission-denied') {
        permissionDeniedKeys.add(permissionKey)
        onPermissionDenied?.()
        console.info('[ChatContext] private chat channels unavailable for this session.')
        return
      }
      console.warn('[Firestore listener failed] private chat channels', workspaceId, error)
    },
  )

  return () => {
    unsubscribePublic()
    unsubscribePrivate()
  }
}

export const getChatChannel = async (
  workspaceId: string,
  channelId: string,
): Promise<ScopedChatChannelDoc | null> => {
  try {
    const channelRef = doc(firestoreDb, 'workspaces', workspaceId, 'channels', channelId)
    const snapshot = await getDoc(channelRef)
    if (!snapshot.exists()) return null
    return {
      id: snapshot.id,
      data: snapshot.data() as Record<string, unknown>,
    }
  } catch (error: unknown) {
    const err = error as { code?: string }
    if (err?.code === 'permission-denied') {
      return null
    }
    console.warn('[Firestore getChatChannel failed]', workspaceId, channelId, error)
    return null
  }
}

export interface PurgeArchivedChannelPayload {
  workspaceId: string
  channelId: string
}

export interface PurgeArchivedChannelResponse {
  deletedMessages: number
}

export const purgeArchivedChannel = async (
  payload: PurgeArchivedChannelPayload,
): Promise<PurgeArchivedChannelResponse> => {
  return callable<PurgeArchivedChannelPayload, PurgeArchivedChannelResponse>(
    'purgeArchivedChannel',
    payload,
    { retries: 0 },
  )
}

